import { useRef, useState } from "react";

import Text from "@/blueprint/components/Text/Text";
import { useAnnounce } from "@/blueprint/components/LiveAnnouncer/useAnnounce";
import { MATIC_DOCK_VARIANT_ID } from "@/consts/shopify";
import type { Product } from "@/graphql/storefront/generated";
import useReferralIncludesBagPass from "@/cart/useReferralIncludesBagPass";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/utils/ShopifyHelpers";
import {
  ADD_ON_SPECS,
  getAddOnCartLines,
  getAddOnsAmount,
  resolveAddOns,
  type AddOnId,
  type AddOnProducts,
  type ResolvedAddOn,
} from "@/views/shop/addOns";
import {
  FLOOR_PACKAGES,
  getFloorPackageNetAmount,
  getFloorPackagePlan,
  type FloorPackageId,
} from "@/views/shop/floorPackages";
import { AddOnDetailsDialog } from "@/views/shop/sections/configurator/AddOnDetailsDialog";
import { ConfiguratorAssurances } from "@/views/shop/sections/configurator/Assurances";
import { SIDE_BY_SIDE_QUERY } from "@/views/shop/sections/configurator/imageColumn";
import {
  DesktopStickyBar,
  StickyCheckout,
  type BarSummary,
  type CheckoutCart,
  type CheckoutTotal,
} from "@/views/shop/sections/configurator/Checkout";
import {
  OptionCardBody,
  Pill,
  PriceWithStrike,
} from "@/views/shop/sections/configurator/OptionCard";
import { OptionRadioGroup } from "@/views/shop/sections/configurator/OptionRadioGroup";
import { OptionCheckCard } from "@/views/shop/sections/configurator/OptionCheckCard";
import ModelSpecBar from "@/views/shop/sections/configurator/ModelSpecBar";
import PaymentTabs from "@/views/shop/sections/configurator/PaymentTabs";
import ShopPayInstallments from "@/components/ShopPayInstallments/ShopPayInstallments";
import {
  lowestMonthlyPayment,
  type ShopPayInstallmentsTerms,
  termsForAmount,
} from "@/lib/shopPayInstallments";
import {
  CONFIGURATOR,
  PAYMENT_TABS,
  type MaticModelId,
  type PaymentTabId,
} from "@/views/shop/shopContent";
import { useTrack, type EventMap } from "@/analytics/events";

type ConfiguratorProps = {
  selectedModelId: MaticModelId;
  onSelectModel: (id: MaticModelId) => void;
  selectedId: FloorPackageId;
  onSelect: (id: FloorPackageId) => void;
  maticProduct?: Product | null | undefined;
  maticSereneProduct: Product | null;
  annualBagPassProduct?: Product | null | undefined;
  maticCareProduct?: Product | null | undefined;
  builderKitProduct?: Product | null | undefined;
  petBrushRollProduct?: Product | null | undefined;
  /** Shopify's installment plans, for the Finance tab's monthly quote. */
  installments: ShopPayInstallmentsTerms | null;
  /** Told of every add-on toggle, so the visual zone can follow it. */
  onAddOnToggle: (id: AddOnId, isAdding: boolean) => void;
  /** Told of the add-on whose details opened, so the zone can show it. */
  onAddOnFocus: (id: AddOnId) => void;
  className?: string;
};

const UNAVAILABLE_LABEL = "Unavailable";

type ProductVariant = NonNullable<Product["variants"]["edges"][number]["node"]>;

const getVariantAmount = (variant: ProductVariant | null): number | null => {
  if (!variant) {
    return null;
  }

  const amount = Number(variant.price.amount);
  return Number.isFinite(amount) ? amount : null;
};

const modelOf = (id: MaticModelId) =>
  CONFIGURATOR.models.find((model) => model.id === id) ??
  CONFIGURATOR.models[0];

const ModelGroup = ({
  heading,
  selectedId,
  onSelect,
  variants,
}: {
  heading: string;
  selectedId: MaticModelId;
  onSelect: (id: MaticModelId) => void;
  variants: Record<MaticModelId, ProductVariant | null>;
}) => (
  <OptionRadioGroup
    label={heading}
    heading={heading}
    selectedId={selectedId}
    onSelect={onSelect}
    options={CONFIGURATOR.models.map((model) => {
      const descriptionId = `model-${model.id}-description`;
      const variant = variants[model.id];
      const amount = getVariantAmount(variant);
      const price =
        amount === null || variant === null
          ? UNAVAILABLE_LABEL
          : formatPrice(amount, variant.price.currencyCode);

      return {
        id: model.id,
        descriptionId,
        isDisabled: !variant?.availableForSale,
        content: (
          <OptionCardBody
            label={model.title}
            descriptionId={descriptionId}
            media={null}
            notes={[model.tagline]}
            priceIsStatus={amount === null}
            price={
              model.shippingNote === null ? (
                price
              ) : (
                <>
                  {price}
                  <Text
                    variant="paragraph-sm"
                    tone="secondary"
                    as="span"
                    className="block italic"
                  >
                    {model.shippingNote}
                  </Text>
                </>
              )
            }
          />
        ),
      };
    })}
  />
);

const FloorPackageGroup = ({
  heading,
  selectedId,
  onSelect,
  maticUnitAmount,
  currencyCode,
  available,
}: {
  heading: string;
  selectedId: FloorPackageId;
  onSelect: (id: FloorPackageId) => void;
  maticUnitAmount: number | null;
  currencyCode: string;
  available: boolean;
}) => {
  const bagPassIncludedFrom = ADD_ON_SPECS.find(
    (spec) => spec.id === "annual-bag-pass",
  )?.includedFromMaticCount;

  return (
    <OptionRadioGroup
      label={heading}
      heading={heading}
      selectedId={selectedId}
      onSelect={onSelect}
      options={FLOOR_PACKAGES.map((floorPackage) => {
        const descriptionId = `floor-package-${floorPackage.id}-description`;

        return {
          id: floorPackage.id,
          descriptionId,
          isDisabled: !available,
          content: (
            <>
              {bagPassIncludedFrom !== undefined &&
              floorPackage.maticCount >= bagPassIncludedFrom ? (
                <Pill tone="bonus">
                  {CONFIGURATOR.bundleBonusLabel(floorPackage.maticCount)}
                </Pill>
              ) : null}
              <OptionCardBody
                label={floorPackage.title}
                descriptionId={descriptionId}
                media={null}
                notes={floorPackage.description}
                priceIsStatus={maticUnitAmount === null}
                price={
                  maticUnitAmount === null
                    ? UNAVAILABLE_LABEL
                    : formatPrice(
                        getFloorPackageNetAmount(
                          floorPackage.id,
                          maticUnitAmount,
                        ),
                        currencyCode,
                      )
                }
              />
            </>
          ),
        };
      })}
    />
  );
};

const AddOnCard = ({
  addOn,
  isSelected,
  onToggle,
}: {
  addOn: ResolvedAddOn;
  isSelected: boolean;
  onToggle: () => void;
}) => {
  const descriptionId = `add-on-${addOn.id}-description`;
  const isLocked = addOn.isIncluded;
  const isUnavailable = !addOn.availableForSale;
  const perMaticNote =
    addOn.priceRule === "per-matic" && !isLocked
      ? addOn.quantity > 1 && addOn.unitAmount > 0
        ? `${formatPrice(addOn.unitAmount, addOn.currencyCode)} ${CONFIGURATOR.perMaticNote}`
        : CONFIGURATOR.perMaticNote
      : null;
  const listPrice =
    addOn.listAmount === 0
      ? CONFIGURATOR.freePriceLabel
      : formatPrice(addOn.listAmount, addOn.currencyCode);

  return (
    <OptionCheckCard
      isSelected={isSelected}
      isLocked={isLocked}
      isUnavailable={isUnavailable}
      descriptionId={descriptionId}
      onToggle={onToggle}
    >
      <OptionCardBody
        label={addOn.name}
        descriptionId={descriptionId}
        media={null}
        notes={addOn.description === "" ? [] : [addOn.description]}
        priceIsStatus={isUnavailable}
        price={
          isUnavailable ? (
            UNAVAILABLE_LABEL
          ) : (
            <span className="inline-flex flex-col items-end whitespace-nowrap">
              {isLocked ? (
                <PriceWithStrike
                  current={CONFIGURATOR.includedLabel}
                  original={listPrice}
                />
              ) : (
                <span>{listPrice}</span>
              )}
              {perMaticNote === null ? null : (
                <Text variant="paragraph-sm" tone="secondary" as="span">
                  {perMaticNote}
                </Text>
              )}
            </span>
          )
        }
      />
    </OptionCheckCard>
  );
};

const AddOnGroup = ({
  heading,
  addOns,
  selectedIds,
  onToggle,
  onOpen,
}: {
  heading: string;
  addOns: readonly ResolvedAddOn[];
  selectedIds: ReadonlySet<AddOnId>;
  onToggle: (addOn: ResolvedAddOn) => void;
  /** Hears which add-on the details dialog shows. */
  onOpen: (id: AddOnId) => void;
}) => {
  // The add-on whose details dialog is open.
  const [openId, setOpenId] = useState<AddOnId | null>(null);
  const track = useTrack();

  const show = (id: AddOnId) => {
    setOpenId(id);
    onOpen(id);
    track("addon_details_opened", { addon_id: id });
  };

  const first = addOns[0];
  if (first === undefined) {
    return null;
  }

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex items-baseline justify-between gap-m">
        <Text variant="paragraph-md" as="p" className="font-semibold">
          {heading}
        </Text>
        {/* The details dialog opens from here, never from a card: a card
            tap is a selection. */}
        <button
          type="button"
          aria-haspopup="dialog"
          onClick={() => show(first.id)}
          className="rounded-4 text-content-primary underline underline-offset-2 hover:text-content-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-black"
        >
          <Text variant="paragraph-sm" tone="inherit" as="span">
            {CONFIGURATOR.addOnsLearnMore}
          </Text>
        </button>
      </div>
      {addOns.map((addOn) => (
        <AddOnCard
          key={addOn.id}
          addOn={addOn}
          isSelected={selectedIds.has(addOn.id)}
          onToggle={() => onToggle(addOn)}
        />
      ))}
      <AddOnDetailsDialog
        addOns={addOns}
        openId={openId}
        selectedIds={selectedIds}
        onShow={show}
        onToggle={onToggle}
        onClose={() => setOpenId(null)}
      />
    </div>
  );
};

const Configurator = ({
  selectedModelId,
  onSelectModel,
  selectedId,
  onSelect,
  maticProduct,
  maticSereneProduct,
  annualBagPassProduct,
  maticCareProduct,
  builderKitProduct,
  petBrushRollProduct,
  installments,
  onAddOnToggle,
  onAddOnFocus,
  className,
}: ConfiguratorProps) => {
  const announce = useAnnounce();
  const reducedMotion = usePrefersReducedMotion();
  // The way to pay picked over the options.
  const [paymentId, setPaymentId] = useState<PaymentTabId>("now");
  const checkoutLocation: EventMap["add_to_cart_clicked"]["button_location"] =
    useMediaQuery(SIDE_BY_SIDE_QUERY) === true
      ? "shop_configurator"
      : "shop_sticky_bar";
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<
    ReadonlySet<AddOnId>
  >(() => new Set<AddOnId>());
  const track = useTrack();
  // A first glance and a deliberated switch look identical without an order.
  const packageSelectionCountRef = useRef(0);

  const addOnProducts: AddOnProducts = {
    "annual-bag-pass": annualBagPassProduct ?? null,
    "matic-care": maticCareProduct ?? null,
    "builder-kit": builderKitProduct ?? null,
    "pet-brush-roll": petBrushRollProduct ?? null,
  };

  const modelVariants: Record<MaticModelId, ProductVariant | null> = {
    matic: maticProduct?.variants.edges[0]?.node ?? null,
    "matic-serene": maticSereneProduct?.variants.edges[0]?.node ?? null,
  };
  const model = modelOf(selectedModelId);
  const maticVariant = modelVariants[selectedModelId];
  const maticBaseAmount = getVariantAmount(maticVariant);
  const currencyCode = maticVariant?.price.currencyCode ?? "USD";
  const selectedFloorPackage =
    FLOOR_PACKAGES.find(({ id }) => id === selectedId) ?? FLOOR_PACKAGES[0];
  const floorPlan = getFloorPackagePlan(selectedFloorPackage.id);
  const maticCount = floorPlan.maticCount;
  const referralIncludesBagPass = useReferralIncludesBagPass();
  const resolvePageAddOns = (count: number) =>
    resolveAddOns(addOnProducts, count, referralIncludesBagPass);
  const addOns = resolvePageAddOns(maticCount);

  const getTotalPrices = (
    floorPackageId: FloorPackageId,
    nextSelectedIds: ReadonlySet<AddOnId>,
  ) => {
    const nextPlan = getFloorPackagePlan(floorPackageId);
    const nextAddOns = resolvePageAddOns(nextPlan.maticCount);
    const amount =
      maticBaseAmount === null
        ? null
        : getFloorPackageNetAmount(floorPackageId, maticBaseAmount) +
          getAddOnsAmount(nextAddOns, nextSelectedIds);

    return {
      currentAmount: amount,
      current:
        amount === null ? UNAVAILABLE_LABEL : formatPrice(amount, currencyCode),
      currencyCode,
    };
  };

  const announceSelection = (
    label: string,
    prices: ReturnType<typeof getTotalPrices>,
  ) => {
    announce(`${label}. Total ${prices.current}.`);
  };

  const handleModelSelect = (id: MaticModelId) => {
    if (id === selectedModelId) return;

    onSelectModel(id);
    const amount = getVariantAmount(modelVariants[id]);
    // The add-ons and packages don't change with the model, only the Matic's
    // unit price does.
    const total =
      amount === null
        ? UNAVAILABLE_LABEL
        : formatPrice(
            getFloorPackageNetAmount(selectedFloorPackage.id, amount) +
              getAddOnsAmount(addOns, selectedAddOnIds),
            modelVariants[id]?.price.currencyCode ?? currencyCode,
          );
    announce(`${modelOf(id).title}. Total ${total}.`);
  };

  const handleFloorPackageSelect = (id: FloorPackageId) => {
    if (id === selectedId) return;

    const floorPackage = FLOOR_PACKAGES.find((item) => item.id === id);
    if (floorPackage === undefined) return;

    onSelect(id);
    const nextPlan = getFloorPackagePlan(id);
    const nextTotals = getTotalPrices(id, selectedAddOnIds);
    track("configurator_package_selected", {
      package_id: id,
      matic_count: nextPlan.maticCount,
      // `dockCount` counts only the extra docks, and every Matic ships with one.
      dock_count: nextPlan.maticCount + nextPlan.dockCount,
      total_amount: nextTotals.currentAmount,
      selection_index: packageSelectionCountRef.current,
      offer_package_id: null,
    });
    packageSelectionCountRef.current += 1;
    announceSelection(floorPackage.title, nextTotals);
  };

  const handleAddOnToggle = (addOn: ResolvedAddOn) => {
    const nextSelectedIds = new Set(selectedAddOnIds);
    const isAdding = !nextSelectedIds.has(addOn.id);

    if (isAdding) {
      nextSelectedIds.add(addOn.id);
    } else {
      nextSelectedIds.delete(addOn.id);
    }

    setSelectedAddOnIds(nextSelectedIds);
    onAddOnToggle(addOn.id, isAdding);
    const nextTotals = getTotalPrices(selectedFloorPackage.id, nextSelectedIds);
    track("configurator_addon_toggled", {
      addon_id: addOn.id,
      is_selected: isAdding,
      addon_amount: addOn.chargedAmount,
      is_included: addOn.isIncluded,
      total_amount: nextTotals.currentAmount,
      offer_package_id: null,
    });
    announceSelection(
      `${addOn.name} ${isAdding ? "added" : "removed"}`,
      nextTotals,
    );
  };

  const {
    currentAmount: totalPriceAmount,
    current: totalPrice,
    currencyCode: totalPriceCurrencyCode,
  } = getTotalPrices(selectedFloorPackage.id, selectedAddOnIds);

  const cartLines = [
    ...(floorPlan.dockCount > 0
      ? [{ variantId: MATIC_DOCK_VARIANT_ID, quantity: floorPlan.dockCount }]
      : []),
    ...getAddOnCartLines(addOns, selectedAddOnIds),
  ];

  const checkoutTotal: CheckoutTotal = {
    amount: totalPriceAmount,
    current: totalPrice,
    currencyCode: totalPriceCurrencyCode,
  };
  // The bars quote the payment pick, as /lp/pet's: for Finance, the lowest
  // monthly Shopify offers on the total in place of it.
  const monthly =
    paymentId === "finance"
      ? lowestMonthlyPayment(
          totalPriceAmount,
          totalPriceCurrencyCode,
          installments,
          PAYMENT_TABS.fallbackMonths,
        )
      : null;
  const barTotal: CheckoutTotal =
    monthly === null
      ? checkoutTotal
      : {
          ...checkoutTotal,
          amount: null,
          current: PAYMENT_TABS.monthlyPrice(monthly),
          lead: PAYMENT_TABS.monthlyLead,
        };
  // With Shopify's plans loaded, Finance's bars carry Shopify's own line
  // ("or 0% APR with shop", its logo and plan pop-up), as the live product
  // page does; otherwise a line on how the pick is paid.
  const financeTerms =
    paymentId === "finance" &&
    installments !== null &&
    totalPriceAmount !== null
      ? termsForAmount(installments, totalPriceAmount, totalPriceCurrencyCode)
      : null;
  const barNote =
    financeTerms !== null ? (
      <ShopPayInstallments
        key={financeTerms.meta}
        terms={financeTerms}
        surface="white"
        hideAmount
      />
    ) : (
      <Text variant="paragraph-sm" tone="secondary" as="span">
        {PAYMENT_TABS.stickyTagline[paymentId]}
      </Text>
    );
  const checkoutCart: CheckoutCart = {
    productId: maticVariant?.id ?? "",
    quantity: maticCount,
    addOnLines: cartLines,
    cartAttributes: { [PAYMENT_TABS.cartAttribute]: paymentId },
    availableForSale: Boolean(maticVariant?.availableForSale),
  };
  const trackAddToCart = (
    location: EventMap["add_to_cart_clicked"]["button_location"],
  ) =>
    track("add_to_cart_clicked", {
      button_location: location,
      package_id: selectedFloorPackage.id,
      addon_ids: [...selectedAddOnIds],
      total_amount: totalPriceAmount,
      offer_package_id: null,
    });
  const barSummary: BarSummary = {
    title: model.title,
    detail: selectedFloorPackage.title,
    note: model.shippingNote,
  };

  // ShopView gives the configurator its fixed 420px grid track from `sd`.
  const columnGap = "gap-[64px] sd:gap-[40px]";
  return (
    <div
      className={cn(
        "flex w-full flex-col sd:max-w-[420px]",
        columnGap,
        className,
      )}
    >
      <div className={cn("flex flex-col", columnGap)}>
        {/* Everything above Add to cart: the payment tabs pin within this
            box, so from `sd` they come to rest one column gap above the
            button rather than over it, and leave with it. */}
        <div className={cn("flex flex-col", columnGap)}>
          {/* Its own row rather than the model block's, so it can pin for
              the whole group from `sd`; the negative margin brings the
              column's gap down to 12 under it while stacked, 24 from `sd`. */}
          <PaymentTabs
            activeId={paymentId}
            onChange={setPaymentId}
            className="-mb-[52px] sd:-mb-[16px]"
          />
          {/* `data-config-block` marks each step for the scroll-linked
              visual zone, which follows whichever crosses its trigger line. */}
          <div data-config-block="model" className="flex flex-col gap-l">
            {/* The same robot either way, so the difference is spelled out
                at the top of the block, as Tesla heads its trims with their
                stats. */}
            <ModelSpecBar modelId={selectedModelId} />
            <ModelGroup
              heading={CONFIGURATOR.modelHeading}
              selectedId={selectedModelId}
              onSelect={handleModelSelect}
              variants={modelVariants}
            />
          </div>
          <div data-config-block="combination">
            <FloorPackageGroup
              heading={CONFIGURATOR.combosHeading}
              selectedId={selectedFloorPackage.id}
              onSelect={handleFloorPackageSelect}
              maticUnitAmount={maticBaseAmount}
              currencyCode={currencyCode}
              available={Boolean(maticVariant?.availableForSale)}
            />
          </div>
          <div data-config-block="addOns">
            <AddOnGroup
              heading={CONFIGURATOR.addOnsHeading}
              addOns={addOns}
              selectedIds={selectedAddOnIds}
              onToggle={handleAddOnToggle}
              onOpen={onAddOnFocus}
            />
          </div>
        </div>
        <StickyCheckout
          total={checkoutTotal}
          barTotal={barTotal}
          summary={barSummary}
          paymentNote={barNote}
          cart={checkoutCart}
          buttonLocation={checkoutLocation}
          onBeforeAdd={() => trackAddToCart(checkoutLocation)}
          notice={null}
        />
        <DesktopStickyBar
          summary={barSummary}
          total={barTotal}
          paymentNote={barNote}
          reducedMotion={reducedMotion}
          cart={checkoutCart}
          onBeforeAdd={() => trackAddToCart("shop_sticky_bar")}
        />
      </div>
      {/* The checkout's in-flow end marks its block: the CTA above it pins to
          the viewport's foot below `sd`, so it never crosses a trigger line. */}
      <div data-config-block="checkout">
        <ConfiguratorAssurances modelId={selectedModelId} />
      </div>
    </div>
  );
};

export default Configurator;
